import { useState, useEffect, useRef } from "react";
import tracker from "../../utils/sanctuary-tracker";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, Headphones } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function BreathingSphere({ onClose }) {
    const { t, language } = useLanguage();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [hasStarted, setHasStarted] = useState(false);
    const [phase, setPhase] = useState("inhale"); // inhale, hold, exhale
    const [secondsActive, setSecondsActive] = useState(0);
    const [hasTriggeredFinish, setHasTriggeredFinish] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const audioRef = useRef(null);
    const requestRef = useRef();

    // 🌐 Select audio track based on language
    const audioTrack = language === 'hi' ? "/Breathe_HinglishandHindi.mp3" : "/Breathe.mp3";

    // 🕒 Track Active Duration
    useEffect(() => {
        let interval;
        if (hasStarted) {
            interval = setInterval(() => {
                setSecondsActive(prev => {
                    const next = prev + 1;
                    // Trigger "Finish" at 3 minutes (180s)
                    if (next >= 180 && !hasTriggeredFinish) {
                        tracker.track('relief_session_finish', { 
                            category: 'Breathing', 
                            duration: 180,
                            language 
                        });
                        setHasTriggeredFinish(true);
                    }
                    return next;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [hasStarted, hasTriggeredFinish, language]);

    // 🧘 High-Frequency Master Clock Sync
    const syncPulse = () => {
        if (!audioRef.current || !hasStarted) return;
        
        // 🧘 Modular Sync: Focus on the 19s cycle, regardless of total file length
        const time = audioRef.current.currentTime % 19;
        
        // 4-7-8 Phase Detection within the 19s cycle
        if (time < 4) {
            if (phase !== "inhale") setPhase("inhale");
        } else if (time < 11) {
            if (phase !== "hold") setPhase("hold");
        } else {
            if (phase !== "exhale") setPhase("exhale");
        }

        requestRef.current = requestAnimationFrame(syncPulse);
    };

    useEffect(() => {
        if (hasStarted) {
            requestRef.current = requestAnimationFrame(syncPulse);
        }
        return () => cancelAnimationFrame(requestRef.current);
    }, [hasStarted, phase]);

    // 🌐 Reload audio when language changes
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.load();
        }
    }, [language]);

    const handleStart = () => {
        setHasStarted(true);
        tracker.trackImpact('START', 'Breathing');
        
        if (audioRef.current) {
            audioRef.current.volume = 1.0;
            audioRef.current.play().catch(e => console.log("Audio play failed", e));
        }
    };

    const toggleMute = () => {
        if (audioRef.current) {
            if (isMuted) {
                audioRef.current.volume = 1.0;
            } else {
                audioRef.current.volume = 0.0;
            }
            setIsMuted(!isMuted);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-[#020617] overflow-hidden">
            {/* 🎭 SCREEN-WIDE ATMOSPHERE */}
            <motion.div 
                animate={{
                    backgroundColor: phase === "inhale" ? "rgba(8, 51, 68, 1)" : phase === "hold" ? "rgba(8, 51, 68, 1)" : "rgba(2, 6, 23, 1)",
                }}
                transition={{ duration: phase === "inhale" ? 4 : phase === "hold" ? 0 : 8, ease: "easeInOut" }}
                className="absolute inset-0 z-0"
            />

            {/* Guided Audio Track */}
            <audio
                ref={audioRef}
                src={audioTrack}
                loop
                onEnded={() => {
                    if (audioRef.current) {
                        audioRef.current.currentTime = 0;
                        audioRef.current.play();
                    }
                }}
                className="hidden"
                onError={(e) => console.error("Audio Error:", e.nativeEvent)}
            />

            <button
                onClick={() => {
                    tracker.trackImpact('CANCEL', 'Breathing');
                    if (!user && hasStarted) {
                        // Soft Gate: If they actually breathed, invite them to stay
                        onClose();
                        navigate('/signup');
                    } else {
                        onClose();
                    }
                }}
                className="absolute top-8 right-8 p-3 text-white/30 hover:text-white transition-all z-50 rounded-full hover:bg-white/5"
            >
                <X className="w-8 h-8" />
            </button>

            {!hasStarted ? (
                // 🚪 ENTRY PORTAL
                <div className="text-center z-10 p-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col items-center"
                    >
                        <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mb-8 border border-cyan-500/20">
                           <Headphones className="w-8 h-8 text-cyan-400" />
                        </div>
                        <h2 className="text-4xl font-display font-light text-white mb-6 tracking-tight">{t('breath_title')}</h2>
                        <p className="text-cyan-100/60 mb-10 max-w-sm text-center italic font-light leading-relaxed">
                            "{t('breath_quote')}"
                        </p>
                        <button
                            onClick={handleStart}
                            className="group relative px-10 py-5 bg-transparent text-white rounded-full font-medium text-xl transition-all overflow-hidden border border-white/20 hover:border-cyan-400/50"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <span className="relative z-10">{t('breath_begin')}</span>
                        </button>
                        <p className="mt-8 text-xs uppercase tracking-[0.2em] text-white/30 flex items-center gap-2">
                             {t('breath_headphones')}
                        </p>
                    </motion.div>
                </div>
            ) : (
                // 🧘 SACRED SPACE (ACTIVE)
                <div className="relative flex flex-col items-center justify-center w-full h-full text-white z-10">
                    
                    {/* Mute Toggle */}
                    <button
                        onClick={toggleMute}
                        className="absolute top-8 left-8 p-3 text-white/30 hover:text-white transition-all flex items-center gap-3 rounded-full hover:bg-white/5"
                    >
                        {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                        <span className="text-[10px] uppercase tracking-[0.3em] font-light">
                            {isMuted ? "Silent" : "Guided"}
                        </span>
                    </button>

                    {/* 💠 THE MULTI-LAYERED AURA */}
                    <div className="relative flex items-center justify-center pointer-events-none">
                        
                        {/* Layer 3: Deep Indigo Base */}
                        <motion.div
                            animate={{
                                scale: phase === "inhale" ? 2.5 : phase === "hold" ? 2.5 : 1.2,
                                opacity: phase === "inhale" ? 0.15 : phase === "hold" ? 0.15 : 0.05,
                            }}
                            transition={{ duration: phase === "inhale" ? 4 : phase === "hold" ? 0.2 : 8, ease: "easeInOut" }}
                            className="absolute w-96 h-96 bg-blue-900 rounded-full blur-[100px]"
                        />

                        {/* Layer 2: Radiant Cyan Glow */}
                        <motion.div
                            animate={{
                                scale: phase === "inhale" ? 1.8 : phase === "hold" ? [1.8, 1.85, 1.8] : 0.9,
                                opacity: phase === "inhale" ? 0.4 : phase === "hold" ? 0.4 : 0.1,
                            }}
                            transition={{ 
                                duration: phase === "inhale" ? 4 : phase === "hold" ? 1.5 : 8, 
                                ease: "easeInOut",
                                repeat: phase === "hold" ? Infinity : 0
                            }}
                            className="absolute w-72 h-72 bg-cyan-400 rounded-full blur-[80px]"
                        />

                        {/* Layer 1: The Core Spirit */}
                        <motion.div
                            animate={{
                                scale: phase === "inhale" ? 1.2 : phase === "hold" ? [1.2, 1.22, 1.2] : 0.8,
                                filter: phase === "hold" ? "blur(30px)" : "blur(40px)",
                            }}
                            transition={{ 
                                duration: phase === "inhale" ? 4 : phase === "hold" ? 1 : 8, 
                                ease: "easeInOut",
                                repeat: phase === "hold" ? Infinity : 0
                            }}
                            className="w-48 h-48 bg-white/20 rounded-full backdrop-blur-xl border border-white/30 shadow-[0_0_80px_rgba(255,255,255,0.2)]"
                        />

                        {/* Subtle Grain/Noise Overlay on Aura */}
                        <div className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
                    </div>

                    {/* 🌬️ BREATH STATE NUDGE (Very Subtle, No Labels) */}
                    <div className="mt-24">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={phase}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 0.4 }}
                                exit={{ opacity: 0 }}
                                className="text-[10px] uppercase tracking-[0.5em] font-light text-white"
                            >
                                {phase === "hold" ? "..." : ""}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                </div>
            )}
        </div>
    );
}
